import type { SignalFilters, SignalSource, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type SignalsService from "../../services/SignalsService";
import createSignalTypesListViewModel from "../signal-types-list/factory";

import type ModifySignalViewModel from "./ModifySignalViewModel";

type GroupedTemplates = Record<number, SignalTemplate<SignalFilters>[]>;

type FactoryDeps = {
    initialTemplateAlias?: string;
    preferredSignalType?: SignalType | null;
    signalsService: SignalsService;
    signalSource: SignalSource;
};

export default function createModifySignalModalViewModel(deps: FactoryDeps): Readonly<ModifySignalViewModel> {
    const { preferredSignalType, signalsService, initialTemplateAlias, signalSource } = deps;
    const templatesBySignalType = signalsService.getAllTemplatesGroupedBySignalType();
    const signalTypesListViewModel = createSignalTypesListViewModel({ signalsService, signalSource });

    function getTemplatesBySignalType(signalType: SignalType | null) {
        if (signalType === null || !templatesBySignalType[signalType]) {
            return [];
        }

        return templatesBySignalType[signalType];
    }

    /**
     * The veto lives here rather than at each caller: `setPreselectedSignalType` is dispatched from the
     * filter panel, the scoring sidebar and the modal's own add CTA, and a locked type reaching any of
     * them would otherwise open a create flow the account cannot use. Twice now a locked type has become
     * reachable because a list upstream changed which types it returns (`62899c1e28`, `a29a866f3f`).
     */
    const isCreatable = (signalType: SignalType | null | undefined): signalType is SignalType =>
        !is.undefined(signalType) &&
        signalType !== null &&
        !signalsService.isSignalTypeLocked(signalType, signalSource);

    return {
        ...signalTypesListViewModel,
        get initiallySelectedType() {
            return isCreatable(preferredSignalType)
                ? preferredSignalType
                : signalTypesListViewModel.initiallySelectedType;
        },

        get signalSource() {
            return signalSource;
        },
        getGroupedTemplatesOfType(signalType: SignalType | null) {
            const templates = getTemplatesBySignalType(signalType);

            if (!templates.length) {
                return [];
            }

            const groupedTemplates = templates.reduce(
                (acum, template): GroupedTemplates => ({
                    ...acum,
                    [template.templateGroup as number]: [...(acum[template.templateGroup as number] || []), template],
                }),
                {} as GroupedTemplates,
            );

            return Object.values(groupedTemplates);
        },
        getDefaultTemplate(signalType: SignalType) {
            return signalsService.getSignalDefaultTemplate(signalType);
        },
        getInitialTemplate(signalType: SignalType) {
            if (is.undefined(initialTemplateAlias)) {
                return null;
            }

            return getTemplatesBySignalType(signalType).find(({ alias }) => alias === initialTemplateAlias) ?? null;
        },
    };
}
